'use client'

import { FC, Fragment, useMemo } from 'react'
import { useGetTranslations, useAppConfig, useCustomRouter } from '@/root/shared-hooks'
import { dateFormats, testIds } from '@/root/shared-consts'
import { dateToFormat } from '@/root/shared-helpers/date/dateHelpers'
import { TicketsIcon } from '@/root/ui/shared/icons/TicketsIcon'
import { RoundTripIcon } from '@/root/ui/shared/icons/RoundTripIcon'
import { CalendarIcon } from '@/root/ui/tuuleliinid/icons/CalendarIcon'
import { ClockIcon } from '@/root/ui/shared/icons/ClockIcon'
import { Reservation } from '@/root/shared-types'
import { getEditCartModel } from '@/root/modules/shared/EditReservation/utils/edit-cart-model'

interface EditCartCardProps {
  reservation?: Reservation
  originalReservation?: Reservation
  isRefundOffered: boolean
  hideFirstLeg?: boolean
  hideSecondLeg?: boolean
}

export const EditCartCard: FC<EditCartCardProps> = ({
  reservation,
  originalReservation,
  isRefundOffered,
  hideFirstLeg,
  hideSecondLeg,
}) => {
  const { getTranslation } = useGetTranslations()
  const { formatMoney } = useAppConfig()
  const { locale } = useCustomRouter()

  // Plates, corrected weights and the driver discount are in the model too; TL does not show them.
  const { legs, totals, isEmpty } = useMemo(
    () => getEditCartModel(reservation, originalReservation, { hideFirstLeg, hideSecondLeg }),
    [reservation, originalReservation, hideFirstLeg, hideSecondLeg]
  )
  const { totalPrice, paidAmount, toPayAmount, toRefundAmount } = totals

  return (
    <div data-testid={testIds.edit.cartCard} className="flex w-full flex-col gap-3">
      <div className="flex items-center gap-2">
        <TicketsIcon width={20} height={24} className="shrink-0" color="neutral-darkest" />
        <h2 className="text-neutral-darkest text-2xl leading-none font-bold">
          {getTranslation('selectedTicketsCartTitle')}
        </h2>
      </div>

      {isEmpty ? (
        <p className="text-neutral-darkest text-base leading-none">{getTranslation('selectedTicketsCartEmpty')}</p>
      ) : (
        <>
          {legs.map((group, index) => (
            <Fragment key={group.seqN}>
              {index > 0 && (
                <div className="flex items-center gap-2 py-1">
                  <RoundTripIcon width={24} height={24} color="primary-base" />
                  <div className="border-primary-base grow border-t" />
                </div>
              )}

              {group.routeTitle && (
                <span data-testid={testIds.edit.cartRouteName} className="text-primary-base text-base font-semibold">
                  {group.routeTitle}
                </span>
              )}

              {group.sailRef && (
                <div className="flex items-center gap-3">
                  <div className="text-neutral-darker flex items-center gap-1.5 text-sm">
                    <CalendarIcon width={16} height={16} color="primary-base" />
                    <span data-testid={testIds.edit.cartDate}>
                      {dateToFormat(group.sailRef.departureDate, dateFormats.DD_DOT_MM_YYYY, locale)}
                    </span>
                  </div>
                  <div className="text-neutral-darker flex items-center gap-1.5 text-sm">
                    <ClockIcon width={16} height={16} color="primary-base" />
                    <span data-testid={testIds.edit.cartTime}>{group.sailRef.departureTime}</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-[1fr_auto_auto] items-start gap-x-4 gap-y-3">
                {group.rows.map((item) => {
                  const textCn = item.isChanged ? 'text-warning-darker' : 'text-neutral-darkest'
                  const bgCn = item.isChanged ? 'bg-warning-very-light rounded' : ''
                  return (
                    <Fragment key={item.priceCategory}>
                      <span
                        data-testid={testIds.edit.cartItemCategory}
                        className={`text-base leading-none ${textCn} ${bgCn}`}
                      >
                        {item.title}
                      </span>
                      <span className={`text-right text-base leading-none whitespace-nowrap ${textCn} ${bgCn}`}>
                        <span data-testid={testIds.edit.cartItemPrice}>{formatMoney(item.pricePerUnit)}</span> x{' '}
                        <span data-testid={testIds.edit.cartItemQuantity}>{item.quantity}</span>
                      </span>
                      <span
                        data-testid={testIds.edit.cartItemSubtotal}
                        className={`text-right text-base leading-none whitespace-nowrap ${textCn} ${bgCn}`}
                      >
                        {formatMoney(item.total)}
                      </span>
                    </Fragment>
                  )
                })}
              </div>

              {group.residentIds.length > 0 && (
                <p data-testid={testIds.edit.cartResidentIds} className="text-primary-base text-sm">
                  {getTranslation('ticketLocalCustomers')} {group.residentIds.join(', ')}
                </p>
              )}
            </Fragment>
          ))}

          <div className="border-neutral-darkest w-full border-t-3 border-dotted" />

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-neutral-darkest text-base leading-none font-semibold">
                {getTranslation('confirmationTotal')}:
              </span>
              <span
                data-testid={testIds.edit.cartTotal}
                className="text-neutral-darkest text-right text-base leading-none font-semibold"
              >
                {formatMoney(totalPrice)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-neutral-darkest text-base leading-none font-semibold">
                {getTranslation('editBillingPreviouslyPaid')}:
              </span>
              <span
                data-testid={testIds.edit.cartPreviouslyPaid}
                className="text-neutral-darkest text-right text-base leading-none font-semibold"
              >
                {formatMoney(paidAmount)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span
                className={`text-base leading-none font-semibold ${toPayAmount > 0 ? 'text-error' : isRefundOffered ? 'text-success' : 'text-neutral-darkest'}`}
              >
                {getTranslation('editBillingPriceDifference')}:
              </span>
              <span
                data-testid={testIds.edit.cartPriceDifference}
                className={`text-right text-base leading-none font-semibold ${toPayAmount > 0 ? 'text-error' : isRefundOffered ? 'text-success' : 'text-neutral-darkest'}`}
              >
                {isRefundOffered ? formatMoney(-toRefundAmount) : formatMoney(toPayAmount)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
